import { combineEpics } from "redux-observable";
import { from, of } from "rxjs";
import { catchError, filter, map, mergeMap, switchMap } from "rxjs/operators";

import { actionCreators, isOfAction } from "../actions";
import { SSO_CLIENT_ID, SSO_URI } from "../app/Config";
import { Epic } from "../utils/types";

// helpers

function ssoUrl(oauthState: string) {
    return `${SSO_URI}/v2/logout/${SSO_CLIENT_ID}?state=${oauthState}`;
}

async function getState() {
    const req = await fetch("/oauth/state", {
        method: "POST",
        cache: "no-cache",
    });
    if (req.status === 200) {
        const json: { state?: string } = await req.json();
        if (json && json.state) {
            return json.state;
        } else {
            throw new Error("no state in response");
        }
    } else {
        throw new Error(`bad response code ${req.status}`);
    }
}

async function logout(token: string) {
    const req = await fetch("/oauth/logout", {
        cache: "no-cache",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: token }),
    });
    if (req.status === 204) {
        return "success";
    } else if (req.status === 400) {
        return "bad_token"; // probably means that user already signed out
    } else {
        throw new Error(`bad response code ${req.status}`);
    }
}

// epics

const onCreateOAuthState: Epic = action$ =>
    action$.pipe(
        filter(isOfAction(actionCreators.createOAuthState)),
        switchMap(() =>
            from(getState()).pipe(
                map(state => actionCreators.redirectToUrl({ url: ssoUrl(state) })),
                catchError(err => of(actionCreators.createOAuthStateFailed({ err })))
            )
        )
    );

const onLogout: Epic = action$ =>
    action$.pipe(
        filter(isOfAction(actionCreators.onLogout)),
        mergeMap(({ payload: { token, isCustomEnvironment } }) => {
            if (token && isCustomEnvironment) {
                logout(token);
            }
            return of();
        })
    );

export const loginEpic = combineEpics(onCreateOAuthState, onLogout);
